import { useCallback, useEffect, useMemo } from 'react';

import { SHOULD_OPEN_PLACE_BID_MODAL } from 'consts/storageKeys';
import { useBezelUser } from 'context/bezelUser';
import { useListing } from 'context/listingContext';
import { useModal } from 'context/modal/ModalContext';
import { useProxyBids } from 'context/proxyBids';
import { navigateToLoginPage } from 'lib/navigateToLoginPage';
import { getFromStorage, removeFromStorage, setToStorage } from 'lib/storageHelpers';
import { Listing, ProxyBid } from 'types';
import { ONE_DAY } from 'utils/date';

import { PlaceBidModal } from './Modals/PlaceBidModal';

import { useLogBidStarted } from '../../hooks/analytics/useLogMaxBidStarted';

export type AuctionsData = {
  live: Listing[];
  ending: Listing[];
  featured: Listing[];
};

export const useGetAuctionsData = (auctions: Listing[]): AuctionsData => {
  const auctionsData = useMemo(() => {
    const liveAuctions: Listing[] = [];
    const endingAuctions: Listing[] = [];
    const justAdded: Listing[] = [];
    const liveAuctionsWithoutJustAdded: Listing[] = [];
    const now = new Date().getTime();

    const sortedAuctions = auctions.sort(
      (a, b) => new Date(a.auctionInfo.endDate).getTime() - new Date(b.auctionInfo.endDate).getTime()
    );

    sortedAuctions?.forEach(auction => {
      if (new Date(auction.auctionInfo.endDate).getTime() - now < ONE_DAY) {
        endingAuctions.push(auction);
      }

      if (new Date(auction.auctionInfo.startDate).getTime() > now - ONE_DAY) {
        justAdded.unshift(auction);
      } else {
        liveAuctionsWithoutJustAdded.unshift(auction);
      }

      if (auction.auctionInfo.live) {
        liveAuctions.unshift(auction);
      }
    });

    const featuredAuctions: Listing[] = endingAuctions
      .concat(justAdded)
      .concat(liveAuctionsWithoutJustAdded)
      .slice(0, 7);

    // Push first element to the end. For some reason swiper library handles order wrong
    const lastElement = featuredAuctions.shift();

    if (lastElement) {
      featuredAuctions.push(lastElement);
    }

    return { live: liveAuctions, ending: endingAuctions, featured: featuredAuctions };
  }, [auctions]);

  return auctionsData;
};

export const useOnPlaceBid = (listing: Listing, bidValue?: number, proxyBidFromProps?: ProxyBid) => {
  const { user } = useBezelUser();
  const { closeModal: closePlaceBidModal, openModal } = useModal();
  const { refreshListing } = useListing();
  const { proxyBid, refetchProxyBids } = useProxyBids();
  const logBidStarted = useLogBidStarted(listing);

  const onClickPlaceBid = useCallback(() => {
    logBidStarted();
    openModal(
      <PlaceBidModal
        listing={listing}
        closeModal={closePlaceBidModal}
        refreshListing={refreshListing}
        bidValue={bidValue}
        proxyBid={proxyBidFromProps || proxyBid}
        refetchProxyBids={refetchProxyBids}
      />
    );
  }, [
    bidValue,
    closePlaceBidModal,
    listing,
    logBidStarted,
    openModal,
    proxyBid,
    proxyBidFromProps,
    refetchProxyBids,
    refreshListing
  ]);
  const onLoginUser = useCallback(() => {
    setToStorage(SHOULD_OPEN_PLACE_BID_MODAL, 'true');
    navigateToLoginPage();
  }, []);

  useEffect(() => {
    // open modal after user clicks on button and logs in
    const shouldOpenModal = getFromStorage(SHOULD_OPEN_PLACE_BID_MODAL);

    if (user && shouldOpenModal === 'true') {
      onClickPlaceBid();
      removeFromStorage(SHOULD_OPEN_PLACE_BID_MODAL);
    }
  }, [onClickPlaceBid, openModal, user]);

  return user ? onClickPlaceBid : onLoginUser;
};
